import React, { useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./EnableForm.module.css";
import { toast } from "react-toastify";
import { BASE_URL_API } from "../../References/URLs";
import AuthContext from "../../store/auth-context";
import ActiveStoreContext from "../../store/ActiveStoreContext";

const EnableForm = (props) => {
    const { onClose } = props
    const [step, setStep] = useState(1);
    const [subdomain, setSubdomain] = useState("");
    const [isDomainAvailable, setIsDomainAvailable] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [isEnabling, setIsEnabling] = useState(false)

    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);

    const activeStore = (() => {
        try {
            return asCtx?.activeStore && asCtx.activeStore.trim() !== ""
                ? JSON.parse(asCtx.activeStore)
                : null;
        } catch {
            return null;
        }
    })();

    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData
        ?.vendorIdentifier;

    const handleNext = async () => {
        if (step === 2) {
            if (!subdomain.trim()) {
                toast.warn("Please enter a subdomain name");
                return;
            }

            // Validate before proceeding
            const available = await checkMiniSiteSubdomainAvailability();
            if (!available) {
                toast.error("This subdomain is not available, please choose another name");
                return;
            }
        }
        setStep((prev) => prev + 1);
    };

    const handleBack = () => setStep((prev) => prev - 1);

    const checkMiniSiteSubdomainAvailability = async () => {
        try {
            setIsChecking(true);
            setIsDomainAvailable(null);

            const response = await fetch(`${BASE_URL_API}checkMiniSiteSubdomainAvailability`, {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    subdomain: subdomain.trim(),
                }),
            });

            if (!response.ok) {
                toast.error("Server error while checking subdomain");
                return false;
            }

            const data = await response.json();

            if (data?.status === "success") {
                if (data?.response === "available") {
                    setIsDomainAvailable(true);
                    toast.success("This subdomain is available!");
                    return true;
                } else {
                    setIsDomainAvailable(false);
                    toast.warn("Subdomain already taken!");
                    return false;
                }
            } else if (data?.message === "tokenExpired") {
                toast.error("Session expired, please login again");
                authCtx.logout();
                return false;
            } else {
                toast.error("Unexpected response from server");
                return false;
            }
        } catch (err) {
            console.error("Error checking subdomain:", err);
            toast.error("Network error while checking availability");
            return false;
        } finally {
            setIsChecking(false);
        }
    };

    // Animation Variants
    const fadeSlide = {
        initial: { opacity: 0, y: 40 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -40 },
        transition: { duration: 0.5, ease: "easeOut" },
    };

    const enableMinisiteNow = async () => {
        setIsEnabling(true)
        const response = await fetch(BASE_URL_API + "enableMinisiteNow",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    subdomain: subdomain,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    createdBy: authCtx?.authIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json();
            if (respo?.status === "success") {
                toast(`Successfully enabled the website!`)
                onClose()
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else if(respo?.message==="this store already have a website enabled!") {
                    toast.warn("This store already have a website enabled!")
                    onClose()
                } else {
                    toast(`Something went wrong : Contact Admin!`);
                }
            }
        }
        setIsEnabling(false)
    }


    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>
                <motion.div
                    className={styles.mainContent}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                >
                    {/* Progress Indicator */}
                    <div className={styles.progressWrapper}>
                        {[1, 2, 3].map((num) => (
                            <div
                                key={num}
                                className={`${styles.stepDot} ${step >= num ? styles.activeStep : ""
                                    }`}
                            ></div>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        {/* STEP 1 */}
                        {step === 1 && (
                            <motion.div key="step1" {...fadeSlide} className={styles.stepContainer}>
                                <h2>✨ Enable Your Salon Microsite</h2>
                                <p className={styles.desc}>
                                    Each salon under Lokaci Pro gets its own personalized microsite like{" "}
                                    <b>yourname.lokacipro.com</b>.
                                    <br />
                                    This becomes your official salon identity — helping clients discover
                                    you, view services, and book appointments easily.
                                </p>
                                <ul className={styles.list}>
                                    <li>Custom subdomain for your salon</li>
                                    <li>Showcase services & team members</li>
                                    <li>Collect reviews and ratings</li>
                                    <li>Integrated with Lokaci Pro CRM</li>
                                </ul>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className={styles.primaryBtn}
                                    onClick={handleNext}
                                >
                                    Get Started →
                                </motion.button>
                            </motion.div>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                            <motion.div key="step2" {...fadeSlide} className={styles.stepContainer}>
                                <h2>Choose Your Subdomain</h2>
                                <p className={styles.desc}>
                                    Pick a unique subdomain for your salon microsite.
                                    Example: <b>glowstudio.lokacipro.com</b>
                                </p>
                                <p className={styles.tinyTxt}>
                                    This is one-time set-up, choose wisely as we don’t recommend changing
                                    your microsite URL (identity).
                                </p>

                                <div className={styles.subdomainBox}>
                                    <input
                                        type="text"
                                        placeholder="Enter your salon name"
                                        value={subdomain}
                                        onChange={(e) => {
                                            const clean = e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, "")
                                                .slice(0, 25);
                                            setSubdomain(clean);
                                            setIsDomainAvailable(null);
                                        }}
                                        className={styles.subdomainInput}
                                        autoFocus
                                    />
                                    <span>.lokacipro.com</span>
                                </div>

                                {/* Availability Status */}
                                {subdomain && (
                                    <div className={styles.availabilityStatus}>
                                        {isChecking ? (
                                            <span className={styles.checking}>Checking availability...</span>
                                        ) : isDomainAvailable === true ? (
                                            <span className={styles.available}>Available</span>
                                        ) : isDomainAvailable === false ? (
                                            <span className={styles.notAvailable}>Not available</span>
                                        ) : (
                                            <motion.button
                                                className={styles.checkBtn}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={checkMiniSiteSubdomainAvailability}
                                            >
                                                Check Availability
                                            </motion.button>
                                        )}
                                    </div>
                                )}

                                <div className={styles.stepButtons}>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className={styles.secondaryBtn}
                                        onClick={handleBack}
                                    >
                                        ← Back
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className={`${styles.primaryBtn} ${isDomainAvailable ? "" : styles.disabledBtn
                                            }`}
                                        onClick={handleNext}
                                        disabled={!isDomainAvailable}
                                    >
                                        Continue →
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 3 */}
                        {step === 3 && (
                            <motion.div key="step3" {...fadeSlide} className={styles.stepContainer}>
                                <h2>Review & Confirm</h2>
                                <p className={styles.desc}>
                                    Please review your details before enabling your microsite.
                                </p>

                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className={styles.reviewBox}
                                >
                                    <p>
                                        <strong>Salon Microsite:</strong>{" "}
                                        <span className={styles.previewLink}>
                                            https://{subdomain || "yourname"}.lokacipro.com
                                        </span>
                                    </p>
                                    <p>
                                        <strong>Status:</strong> Not Yet Enabled
                                    </p>
                                </motion.div>

                                {isEnabling === false ?
                                    <div className={styles.stepButtons}>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={styles.secondaryBtn}
                                            onClick={handleBack}
                                        >
                                            ← Back
                                        </motion.button>

                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={styles.primaryBtn}
                                            onClick={() => enableMinisiteNow()}
                                        >
                                            Enable Now
                                        </motion.button>
                                    </div>
                                    :
                                    <div className={styles.stepButtons}>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={styles.secondaryBtn}
                                            disabled
                                        >
                                            ← Back
                                        </motion.button>

                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={styles.primaryBtn}
                                            disabled
                                        >
                                            Enabling...
                                        </motion.button>
                                    </div>
                                }
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </div>
    );
};

export default EnableForm;
